/**
 * Asset registry — spec §5.5.
 *
 * Route: `/assets`. A dense, sortable table of hardware (vehicle, camera,
 * compute, gps, router) with status, location and owning city. Filters live in
 * the URL so a filtered registry is linkable. Expanding a row lazily loads the
 * tickets that reference that asset (`GET /api/v1/assets/{id}/tickets`), and the
 * city cell links straight through to the city page.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import '../../components/graph/graph.css'
import { AssetStatusChip, Card, Placeholder, TicketRow } from '../city/parts.jsx'
import {
  ASSET_KINDS,
  ASSET_STATUSES,
  ASSET_STATUS_META,
  apiGet,
  cls,
  qs,
  useAsync,
} from '../city/shared.js'

const COLUMNS = [
  { key: 'kind', label: 'Kind', width: 96 },
  { key: 'model', label: 'Model' },
  { key: 'serial', label: 'Serial', width: 150 },
  { key: 'status', label: 'Status', width: 120 },
  { key: 'location', label: 'Location', width: 160 },
  { key: 'city', label: 'City', width: 150 },
]

/** Read repeated query params as an array. */
function multi(params, key) {
  return params.getAll(key).filter(Boolean)
}

export default function AssetsPage() {
  const [params, setParams] = useSearchParams()
  const kinds = multi(params, 'kind')
  const statuses = multi(params, 'status')
  const city = params.get('city') || ''
  const query = params.get('q') || ''
  const sort = params.get('sort') || 'kind'
  const dir = params.get('dir') === 'desc' ? 'desc' : 'asc'
  const focus = params.get('focus')

  const [expanded, setExpanded] = useState(() => (focus ? new Set([Number(focus)]) : new Set()))

  const cities = useAsync(() => apiGet('/cities'), [])
  const assets = useAsync(
    () => apiGet(`/assets${qs({ kind: kinds, status: statuses, city: city || undefined })}`),
    [kinds.join(','), statuses.join(','), city],
  )

  const cityById = useMemo(() => {
    const map = new Map()
    for (const c of cities.data || []) map.set(c.id, c)
    return map
  }, [cities.data])

  const setParam = useCallback(
    (mutate) => {
      const next = new URLSearchParams(params)
      mutate(next)
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const toggleValue = (key, value) =>
    setParam((next) => {
      const current = next.getAll(key)
      next.delete(key)
      const after = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
      after.forEach((v) => next.append(key, v))
    })

  const rows = useMemo(() => {
    let list = [...(assets.data || [])]
    if (query) {
      const q = query.toLowerCase()
      list = list.filter((a) =>
        [a.model, a.serial, a.location, a.kind, a.notes]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      )
    }
    const value = (a) => {
      if (sort === 'city') return cityById.get(a.assigned_city_id)?.slug || ''
      return a[sort] == null ? '' : String(a[sort])
    }
    list.sort((a, b) => value(a).localeCompare(value(b)) || a.id - b.id)
    if (dir === 'desc') list.reverse()
    return list
  }, [assets.data, query, sort, dir, cityById])

  const byStatus = useMemo(() => {
    const counts = Object.fromEntries(ASSET_STATUSES.map((s) => [s, 0]))
    for (const a of assets.data || []) counts[a.status] = (counts[a.status] || 0) + 1
    return counts
  }, [assets.data])

  useFocusScroll(focus, assets.data)

  const toggleRow = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const sortBy = (key) =>
    setParam((next) => {
      if (next.get('sort') === key) next.set('dir', dir === 'asc' ? 'desc' : 'asc')
      else {
        next.set('sort', key)
        next.set('dir', 'asc')
      }
    })

  return (
    <div className="pm-page">
      <header className="pm-page-head">
        <div>
          <h1 className="pm-title">Assets</h1>
          <p className="pm-sub">
            {rows.length} of {(assets.data || []).length} hardware items ·{' '}
            {ASSET_STATUSES.filter((s) => byStatus[s])
              .map((s) => `${byStatus[s]} ${ASSET_STATUS_META[s].label.toLowerCase()}`)
              .join(' · ') || 'none registered'}
          </p>
        </div>
      </header>

      <div className="pm-toolbar">
        <input
          className="input"
          placeholder="Search model, serial, location…"
          value={query}
          onChange={(e) =>
            setParam((next) => {
              if (e.target.value) next.set('q', e.target.value)
              else next.delete('q')
            })
          }
          style={{ minWidth: 240 }}
        />
        <div className="pm-seg">
          {ASSET_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className="btn"
              aria-pressed={kinds.includes(k)}
              onClick={() => toggleValue('kind', k)}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="pm-seg">
          {ASSET_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className="btn"
              aria-pressed={statuses.includes(s)}
              onClick={() => toggleValue('status', s)}
            >
              {ASSET_STATUS_META[s].label}
            </button>
          ))}
        </div>
        <select
          className="select"
          value={city}
          onChange={(e) =>
            setParam((next) => {
              if (e.target.value) next.set('city', e.target.value)
              else next.delete('city')
            })
          }
        >
          <option value="">All cities</option>
          {(cities.data || []).map((c) => (
            <option key={c.id} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>
        {kinds.length || statuses.length || city || query ? (
          <button type="button" className="btn" onClick={() => setParams({}, { replace: true })}>
            Clear
          </button>
        ) : null}
      </div>

      <Card bodyClass="">
        <Placeholder
          loading={assets.loading}
          error={assets.error}
          empty={!assets.loading && !assets.error && rows.length === 0}
        >
          No assets match these filters.
        </Placeholder>
        {rows.length ? (
          <table className="pm-table">
            <thead>
              <tr>
                <th style={{ width: 24 }} />
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className="pm-sortable"
                    style={col.width ? { width: col.width } : undefined}
                    onClick={() => sortBy(col.key)}
                    title={`Sort by ${col.label.toLowerCase()}`}
                  >
                    {col.label}
                    {sort === col.key ? (dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((asset) => {
                const owner = cityById.get(asset.assigned_city_id)
                const open = expanded.has(asset.id)
                return (
                  <Fragment key={asset.id}>
                    <tr
                      className={cls(String(asset.id) === focus && 'is-active')}
                      onClick={() => toggleRow(asset.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td aria-hidden>{open ? '▾' : '▸'}</td>
                      <td className="key">{asset.kind}</td>
                      <td style={{ fontWeight: 600 }}>{asset.model}</td>
                      <td className="key">{asset.serial || '—'}</td>
                      <td>
                        <AssetStatusChip status={asset.status} />
                      </td>
                      <td>{asset.location || '—'}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {owner ? (
                          <Link to={`/city/${owner.slug}`} style={{ color: 'inherit' }}>
                            {owner.name}
                          </Link>
                        ) : (
                          <span className="pm-sub">unassigned</span>
                        )}
                      </td>
                    </tr>
                    {open ? (
                      <tr>
                        <td className="pm-expand-cell" colSpan={COLUMNS.length + 1}>
                          <div className="pm-expand-inner">
                            <AssetTickets asset={asset} />
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        ) : null}
      </Card>
    </div>
  )
}

/** The expanded drill-down: every ticket referencing one asset. */
function AssetTickets({ asset }) {
  const { data, loading, error } = useAsync(() => apiGet(`/assets/${asset.id}/tickets`), [asset.id])
  return (
    <div>
      {asset.notes ? (
        <p className="pm-sub" style={{ marginTop: 0 }}>
          {asset.notes}
        </p>
      ) : null}
      <div className="pm-card-head" style={{ padding: '4px 0', borderBottom: 0 }}>
        Tickets referencing {asset.model}
        {data ? ` · ${data.length}` : ''}
      </div>
      <Placeholder loading={loading} error={error} empty={!!data && !data.length}>
        No tickets reference this asset.
      </Placeholder>
      {data && data.length ? (
        <div className="pm-tickets">
          {data.map((t) => (
            <TicketRow key={t.id} ticket={t} showStatus showCities />
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Scroll a deep-linked asset row (``?focus=<id>``) into view once loaded. */
function useFocusScroll(focus, loaded) {
  useEffect(() => {
    if (!focus || !loaded) return
    const el = document.querySelector('tr.is-active')
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' })
  }, [focus, loaded])
}
